<template>
  <div>
    <!-- En-tête : compteur, recherche, filtres et actions. -->
    <div class="px-3 pb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">
          Contacts <small class="text-muted">({{ filtered.length }} / {{ contacts.length }})</small>
        </h5>
        <b-form-input
          v-model="searchQuery"
          size="sm"
          type="search"
          placeholder="Search name, email, subject..."
          style="max-width: 320px;"
        ></b-form-input>
      </div>
      <!-- Filtres : plage de dates des messages. -->
      <div class="d-flex flex-wrap align-items-center">
        <label class="small text-muted mb-0 mr-1">From</label>
        <b-form-input v-model="dateFrom" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <label class="small text-muted mb-0 mr-1">To</label>
        <b-form-input v-model="dateTo" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <button
          type="button"
          class="btn btn-light btn-sm mr-2 mb-2 mb-md-0"
          v-if="hasActiveFilters"
          @click="clearFilters"
        ><feather type="x" size="14" class="mr-1"></feather>Clear</button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm mb-2 mb-md-0 ml-auto"
          :disabled="!filtered.length"
          @click="exportCsv"
        ><feather type="download" size="14" class="mr-1"></feather>Export CSV</button>
      </div>
      <p class="text-danger mb-0 mt-2" v-if="actionError">{{ actionError }}</p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted mt-3 mb-0">Loading contact messages...</p>
    </div>
    <div v-else-if="loadError" class="text-center py-5">
      <p class="text-danger mb-0">{{ loadError }}</p>
    </div>
    <template v-else>
      <div class="table-responsive datatable-vue text-center">
        <b-table
          ref="TableContacts"
          show-empty
          striped
          hover
          head-variant="light"
          bordered
          stacked="md"
          :items="filtered"
          :fields="tablefields"
          :current-page="currentPage"
          :per-page="perPage"
        >
          <template #empty>
            <span>No contact messages for this store yet.</span>
          </template>
          <template #cell(date)="field">{{ formatDate(field.item.date) }}</template>
          <template #cell(name)="field">
            <div style="font-weight:600;">{{ field.item.name || "—" }}</div>
          </template>
          <template #cell(email)="field">
            <a v-if="field.item.email" :href="'mailto:' + field.item.email + (field.item.subject ? '?subject=' + encodeURIComponent('Re: ' + field.item.subject) : '')">{{ field.item.email }}</a>
            <span v-else>—</span>
          </template>
          <template #cell(subject)="field">{{ field.item.subject || "—" }}</template>
          <template #cell(message)="field">
            <div class="text-truncate" style="max-width: 320px;" :title="field.item.message">{{ field.item.message || "—" }}</div>
          </template>
          <template #cell(actions)="field">
            <div class="row-actions">
              <button type="button" class="icon-btn" title="View message" @click="openMessage(field.item)">
                <feather type="eye" size="16"></feather>
              </button>
              <button type="button" class="icon-btn icon-btn-danger" title="Delete message" @click="confirmDelete(field.item)">
                <feather type="trash-2" size="16"></feather>
              </button>
            </div>
          </template>
        </b-table>
      </div>
      <b-col md="12" class="my-1 p-0 pagination-justify" v-if="filtered.length > perPage">
        <b-pagination
          v-model="currentPage"
          :total-rows="filtered.length"
          :per-page="perPage"
          aria-controls="TableContacts"
          class="mt-4"
        ></b-pagination>
      </b-col>
    </template>

    <!-- Détail d'un message de contact. -->
    <b-modal ref="contactModal" title="Contact message" size="lg" ok-only ok-title="Close">
      <div v-if="current">
        <p class="mb-1"><strong>Name:</strong> {{ current.name || "—" }}</p>
        <p class="mb-1"><strong>Email:</strong> {{ current.email || "—" }}</p>
        <p class="mb-1"><strong>Subject:</strong> {{ current.subject || "—" }}</p>
        <p class="mb-1"><strong>Date:</strong> {{ formatDate(current.date) }}</p>
        <hr>
        <p class="mb-0" style="white-space: pre-wrap;">{{ current.message || "—" }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import { parseOrderDate, downloadCSV, inDateRange } from "../../../utils/store-stats";

export default {
  name: "table-contacts",
  props: ["site"],
  data() {
    return {
      tablefields: [
        { key: "date", label: "Date", sortable: true },
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "subject", label: "Subject", sortable: true },
        { key: "message", label: "Message" },
        { key: "actions", label: "" }
      ],
      contacts: [],
      current: null,
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      perPage: 25,
      loading: false,
      loadError: '',
      actionError: ''
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(this.searchQuery.trim() || this.dateFrom || this.dateTo);
    },
    // Filtrage local : messages du store sélectionné (domaine nu ou www) ;
    // les anciens messages migrés sans store restent visibles partout.
    filtered() {
      var q = this.searchQuery.trim().toLowerCase();
      var hosts = [this.site, 'www.' + this.site];
      return this.contacts.filter((c) => {
        if (c.site && hosts.indexOf(c.site) === -1) return false;
        if ((this.dateFrom || this.dateTo) && !inDateRange(c.date, this.dateFrom, this.dateTo)) return false;
        if (!q) return true;
        var hay = (c.name + ' ' + c.email + ' ' + c.subject + ' ' + c.message).toLowerCase();
        return hay.indexOf(q) !== -1;
      });
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    dateFrom() {
      this.currentPage = 1;
    },
    dateTo() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.loadContacts();
  },
  methods: {
    // Date lisible d'un message (les anciens messages n'en ont pas).
    formatDate(value) {
      if (!value) return "—";
      try {
        return value.toLocaleDateString();
      } catch (e) {
        return "—";
      }
    },
    // Remet tous les filtres à zéro (recherche incluse).
    clearFilters() {
      this.searchQuery = '';
      this.dateFrom = '';
      this.dateTo = '';
    },
    openMessage(contact) {
      this.current = contact;
      this.$refs.contactModal.show();
    },
    // Suppression avec confirmation : le document Contacts est effacé via la
    // passerelle (collection Contacts, comme la suppression des articles).
    confirmDelete(contact) {
      var label = contact.email || contact.name || contact.document;
      this.$bvModal.msgBoxConfirm('Delete message from ' + label + ' ?', {
        title: 'Confirmation',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'DELETE',
        cancelTitle: 'CANCEL',
        footerClass: 'p-2',
        hideHeaderClose: false
      })
        .then((value) => {
          if (value) this.deleteContact(contact);
        })
        .catch(() => {});
    },
    async deleteContact(contact) {
      this.actionError = '';
      try {
        await firebase.firestore().collection('Contacts').doc(contact.document).delete();
        this.contacts = this.contacts.filter((c) => c.document !== contact.document);
        // Si la modale de détail montrait ce message, on la referme.
        if (this.current && this.current.document === contact.document) {
          this.current = null;
          this.$refs.contactModal.hide();
        }
      } catch (error) {
        this.actionError = 'Unable to delete message (' + ((error && error.message) || error) + ').';
      }
    },
    // Export CSV des messages filtrés.
    exportCsv() {
      var rows = this.filtered.map((c) => ({
        Date: c.date ? c.date.toLocaleDateString() : '',
        Name: c.name,
        Email: c.email,
        Subject: c.subject,
        Message: c.message,
        Store: c.site || ''
      }));
      downloadCSV(rows, 'contacts-' + this.site + '-' + new Date().toISOString().slice(0, 10) + '.csv');
    },
    // Messages envoyés via le formulaire « Contact us » de la vitrine
    // (collection Contacts). Le site est enregistré depuis octobre 2026 ;
    // les messages plus anciens (migrés de Firestore) n'en ont pas.
    async loadContacts() {
      this.loading = true;
      this.loadError = '';
      try {
        var list = [];
        await firebase.firestore().collection('Contacts')
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              var c = doc.data() || {};
              list.push({
                document: doc.id,
                name: c.nameInput || c.name || '',
                email: c.emailInput || c.email || '',
                subject: c.subjectInput || c.subject || '',
                message: c.messageInput || c.message || '',
                site: String(c.site || c.host || '').toLowerCase(),
                date: parseOrderDate(c.created || c.date)
              });
            });
          });
        // Tri : messages les plus récents en premier, sans date en fin.
        this.contacts = list.sort((a, b) => {
          var ta = a.date ? a.date.getTime() : 0;
          var tb = b.date ? b.date.getTime() : 0;
          if (ta !== tb) return tb - ta;
          return (a.name || '').localeCompare(b.name || '');
        });
      } catch (error) {
        this.loadError = 'Unable to load contact messages (' + ((error && error.message) || error) + ').';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style></style>
